import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";

const COLORS = {
  primary: "#4f46e5",
  accent: "#f97316",
  success: "#22c55e",
  danger: "#ef4444",
  info: "#06b6d4",
  amber: "#f59e0b",
};

const PIE_COLORS = ["#4f46e5", "#22c55e", "#f97316", "#06b6d4", "#a855f7", "#ef4444"];

const formatNumber = (value) => {
  const n = typeof value === "string" ? Number(value) : value;
  if (n === null || n === undefined || !Number.isFinite(n)) return value ?? "—";
  return n.toLocaleString();
};

const formatMoney = (value) => {
  const n = typeof value === "string" ? Number(value) : value;
  if (n === null || n === undefined || !Number.isFinite(n)) return value ?? "—";
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const asNumber = (value) => {
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
};

const Card = ({
  title,
  value,
  sub,
  className = "",
  titleClassName = "",
  valueClassName = "",
  subClassName = "",
}) => {
  return (
    <div className={`rounded-xl bg-white border border-gray-200 p-4 ${className}`.trim()}>
      <div
        className={`text-xs uppercase tracking-wide ${titleClassName || "text-slate-700"}`.trim()}
      >
        {title}
      </div>
      <div
        className={`mt-1 text-2xl font-semibold ${valueClassName || "text-slate-900"}`.trim()}
        style={
          (valueClassName || "").includes("text-white")
            ? { textShadow: "0 1px 2px rgba(0,0,0,0.35)" }
            : undefined
        }
      >
        {value}
      </div>
      {sub ? (
        <div
          className={`mt-1 text-xs ${subClassName || titleClassName || "text-slate-700"}`.trim()}
          style={
            (subClassName || titleClassName || "").includes("text-white")
              ? { textShadow: "0 1px 2px rgba(0,0,0,0.3)" }
              : undefined
          }
        >
          {sub}
        </div>
      ) : null}
    </div>
  );
};

const Section = ({ title, right, children, tone = "indigo" }) => {
  const toneClass =
    tone === "emerald"
      ? "bg-emerald-50 border-emerald-200"
      : tone === "amber"
        ? "bg-amber-50 border-amber-200"
        : tone === "rose"
          ? "bg-rose-50 border-rose-200"
          : tone === "cyan"
            ? "bg-cyan-50 border-cyan-200"
            : "bg-indigo-50 border-indigo-200";
  const titleClass =
    tone === "emerald"
      ? "text-emerald-900"
      : tone === "amber"
        ? "text-amber-900"
        : tone === "rose"
          ? "text-rose-900"
          : tone === "cyan"
            ? "text-cyan-900"
            : "text-indigo-900";

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className={`px-4 py-3 border-b flex items-center gap-3 ${toneClass}`.trim()}>
        <div className={`font-semibold ${titleClass}`.trim()}>{title}</div>
        <div className="ml-auto text-xs text-slate-700">{right}</div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
};

const StatRow = ({ label, value }) => {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
      <div className="text-sm text-slate-700">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
};

const readJsonSafe = async (res) => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const AppStatistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const pollRef = useRef(null);
  const abortRef = useRef(null);

  const fetchLatest = useCallback(async () => {
    setError(null);

    try {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const res = await fetch(`${API_URL}/application_statistics/get_last_statistic`, {
        method: "GET",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await readJsonSafe(res);
      if (!res.ok) {
        throw new Error(data?.message || `HTTP ${res.status}`);
      }

      const record = Array.isArray(data) ? data[0] : data;
      setStats(record || null);
      setLastUpdatedAt(new Date());
    } catch (e) {
      if (e?.name === "AbortError") return;
      setError(e?.message || "Failed to load application statistics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatest();
    pollRef.current = setInterval(fetchLatest, 15000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchLatest]);

  const tripsChart = useMemo(() => {
    const t = stats?.trips || {};
    const privateCount = asNumber(t?.private?.count);
    const rideCount = asNumber(t?.rideshare?.count);
    const privateBilled = asNumber(t?.private?.billed);
    const rideBilled = asNumber(t?.rideshare?.billed);
    return [
      { name: "Private", trips: privateCount, billed: privateBilled },
      { name: "Rideshare", trips: rideCount, billed: rideBilled },
    ];
  }, [stats]);

  const tripsPie = useMemo(() => {
    const t = stats?.trips || {};
    const privateCount = asNumber(t?.private?.count);
    const rideCount = asNumber(t?.rideshare?.count);
    return [
      { name: "Private", value: privateCount },
      { name: "Rideshare", value: rideCount },
    ];
  }, [stats]);

  const walletsTotals = stats?.wallets?.totals || {};
  const userActivity = stats?.user_activity || {};
  const complaints = stats?.complaints || {};
  const newCustomers = stats?.new_customers || {};
  const newDrivers = stats?.new_drivers || {};

  const headerRight = lastUpdatedAt
    ? `Last updated: ${lastUpdatedAt.toLocaleTimeString()}`
    : "";

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-[#002966]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        <div className="max-w-full mx-auto px-2 py-3 md:px-3 md:py-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div>
              <div className="text-2xl font-bold text-slate-900">Live App Statistics</div>
              <div className="text-sm text-gray-600">
                Endpoint: <code>/application_statistics/get_last_statistic</code>
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <div className="text-xs text-gray-500">{headerRight}</div>
              <button
                type="button"
                onClick={fetchLatest}
                className="px-4 py-2 rounded-md text-white text-sm font-semibold shadow-sm bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700"
              >
                Refresh
              </button>
            </div>
          </div>

          {error ? (
            <div className="mt-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
              {error}
            </div>
          ) : null}

          {!stats ? (
            <div className="mt-6 p-4 rounded bg-white border border-gray-200 text-gray-700">
              No statistics available.
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card
                  title="Total Trips"
                  value={formatNumber(stats.total_trips)}
                  sub={String(stats.date || "")}
                  className="border-0 text-white bg-gradient-to-br from-indigo-600 to-blue-500"
                  titleClassName="text-white/95"
                  valueClassName="text-white"
                  subClassName="text-white/95"
                />
                <Card
                  title="Total Billed"
                  value={formatMoney(stats.total_billed)}
                  sub="All trips billed"
                  className="border-0 text-white bg-gradient-to-br from-amber-500 to-orange-500"
                  titleClassName="text-white/95"
                  valueClassName="text-white"
                  subClassName="text-white/95"
                />
                <Card
                  title="Trips / Customer"
                  value={formatNumber(stats.trips_per_customer)}
                  sub="Average trips per customer"
                  className="border-0 text-white bg-gradient-to-br from-emerald-500 to-teal-500"
                  titleClassName="text-white/95"
                  valueClassName="text-white"
                  subClassName="text-white/95"
                />
                <Card
                  title="Avg Trip Rate"
                  value={formatMoney(stats.average_trip_rate_usd)}
                  sub="USD"
                  className="border-0 text-white bg-gradient-to-br from-fuchsia-500 to-purple-500"
                  titleClassName="text-white/95"
                  valueClassName="text-white"
                  subClassName="text-white/95"
                />
              </div>

              <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Section title="Trips Breakdown" right={headerRight} tone="indigo">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div style={{ width: "100%", height: 260 }}>
                      <ResponsiveContainer>
                        <BarChart data={tripsChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="trips" fill={COLORS.primary} name="Trips" radius={[6, 6, 0, 0]} />
                          <Bar dataKey="billed" fill={COLORS.accent} name="Billed" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div style={{ width: "100%", height: 260 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Tooltip />
                          <Legend />
                          <Pie
                            data={tripsPie}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={90}
                            label
                          >
                            {tripsPie.map((entry, index) => (
                              <Cell
                                key={`${entry.name}-${index}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Private</div>
                      <StatRow label="Trips" value={formatNumber(stats?.trips?.private?.count)} />
                      <StatRow label="Billed" value={formatMoney(stats?.trips?.private?.billed)} />
                      <StatRow label="% of Trips" value={`${stats?.trips?.private?.percentage_of_total ?? "—"}%`} />
                      <StatRow label="% of Billed" value={`${stats?.trips?.private?.billed_percentage ?? "—"}%`} />
                    </div>

                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Rideshare</div>
                      <StatRow label="Trips" value={formatNumber(stats?.trips?.rideshare?.count)} />
                      <StatRow label="Billed" value={formatMoney(stats?.trips?.rideshare?.billed)} />
                      <StatRow label="% of Trips" value={`${stats?.trips?.rideshare?.percentage_of_total ?? "—"}%`} />
                      <StatRow label="% of Billed" value={`${stats?.trips?.rideshare?.billed_percentage ?? "—"}%`} />
                    </div>
                  </div>
                </Section>

                <Section title="New Users" right={headerRight} tone="emerald">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Customers</div>
                      <StatRow label="Total" value={formatNumber(newCustomers.total)} />
                      <StatRow label="Private" value={formatNumber(newCustomers.private)} />
                      <StatRow label="Rideshare" value={formatNumber(newCustomers.rideshare)} />
                    </div>
                    <div className="rounded-lg bg-cyan-50 border border-cyan-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Drivers</div>
                      <StatRow label="Total" value={formatNumber(newDrivers.total)} />
                      <StatRow label="Private" value={formatNumber(newDrivers.private)} />
                      <StatRow label="Rideshare" value={formatNumber(newDrivers.rideshare)} />
                    </div>
                  </div>
                </Section>

                <Section title="User Activity & Complaints" right={headerRight} tone="amber">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Activity</div>
                      <StatRow label="Signed up today" value={formatNumber(userActivity.signed_up_today)} />
                      <StatRow label="Logged in today" value={formatNumber(userActivity.logged_in_today)} />
                      <StatRow label="Logged in 2 days ago" value={formatNumber(userActivity.logged_in_2_days_ago)} />
                      <StatRow label="Logged in 30 days ago" value={formatNumber(userActivity.logged_in_30_days_ago)} />
                      <StatRow label="Avg engagement days" value={formatNumber(userActivity.average_engagement_days)} />
                    </div>

                    <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Complaints</div>
                      <StatRow label="Total" value={formatNumber(complaints.total)} />
                      <StatRow label="Private" value={formatNumber(complaints.private)} />
                      <StatRow label="Rideshare" value={formatNumber(complaints.rideshare)} />
                    </div>
                  </div>
                </Section>
              </div>

              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Wallets (Driver/Customer/Company)" right={headerRight} tone="cyan">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="rounded-lg bg-cyan-50 border border-cyan-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Driver Wallet</div>
                      <StatRow label="Open balance" value={formatMoney(stats?.wallets?.driver?.open_balance)} />
                      <StatRow label="Top up" value={formatMoney(stats?.wallets?.driver?.top_up)} />
                      <StatRow label="Billed charges" value={formatMoney(stats?.wallets?.driver?.billed_charges)} />
                      <StatRow label="Withdrawals" value={formatMoney(stats?.wallets?.driver?.withdrawals)} />
                      <StatRow label="Close balance" value={formatMoney(stats?.wallets?.driver?.close_balance)} />
                    </div>

                    <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Customer Wallet</div>
                      <StatRow label="Open balance" value={formatMoney(stats?.wallets?.customer?.open_balance)} />
                      <StatRow label="Billed charges" value={formatMoney(stats?.wallets?.customer?.billed_charges)} />
                      <StatRow label="Withdrawals" value={formatMoney(stats?.wallets?.customer?.withdrawals)} />
                      <StatRow label="Promo" value={formatMoney(stats?.wallets?.customer?.promo)} />
                      <StatRow label="Close balance" value={formatMoney(stats?.wallets?.customer?.close_balance)} />
                    </div>

                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Company Wallet</div>
                      <StatRow label="Open balance" value={formatMoney(stats?.wallets?.company?.open_balance)} />
                      <StatRow label="Income from charges" value={formatMoney(stats?.wallets?.company?.income_from_charges)} />
                      <StatRow label="Promotions" value={formatMoney(stats?.wallets?.company?.promotions)} />
                      <StatRow label="Withdraws out" value={formatMoney(stats?.wallets?.company?.withdraws_out)} />
                      <StatRow label="Close balance" value={formatMoney(stats?.wallets?.company?.close_balance)} />
                    </div>
                  </div>
                </Section>

                <Section title="Wallet Totals" right={headerRight} tone="rose">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Balances</div>
                      <StatRow label="User wallet total" value={formatMoney(walletsTotals.user_wallet_total_balance)} />
                      <StatRow label="Main wallet total" value={formatMoney(walletsTotals.main_wallet_total_balance)} />
                      <StatRow label="Revenue wallet total" value={formatMoney(walletsTotals.revenue_wallet_total_balance)} />
                      <StatRow label="Vendor wallet total" value={formatMoney(walletsTotals.vendor_wallet_total_balance)} />
                      <StatRow label="Escrow total" value={formatMoney(walletsTotals.escrow_total_balance)} />
                    </div>

                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Flows</div>
                      <StatRow label="Top up (user wallet)" value={formatMoney(walletsTotals.sum_top_up_uw)} />
                      <StatRow label="Withdrawals (user wallet)" value={formatMoney(walletsTotals.sum_withdrawal_uw)} />
                      <StatRow label="Commission (user wallet)" value={formatMoney(walletsTotals.sum_commission_uw)} />
                      <StatRow label="Promotions (user wallet)" value={formatMoney(walletsTotals.sum_promotion_uw)} />
                      <StatRow label="Mischief penalty (user wallet)" value={formatMoney(walletsTotals.sum_mischief_penalty_uw)} />
                    </div>
                  </div>
                </Section>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AppStatistics;